import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { LocalAccount } from '../../../../types/LocalAccount';
import {
    Badge,
    ButtonSizes,
    ButtonStyles,
    EntityChip,
    List,
    ListItem,
    MenuButton,
    Status,
    StatusType
} from '@silverfort/sf-components';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { isMfaModuleEnabled } from '../../../../utils';
import { usePricingModulesState } from '../../../../../../context/contexts/PricingModuleContext';
import { isUserPermitted } from '../../../../../../TowerMenu/TowerMenuUtils';
import { Entity, UserSession } from '../../../../../../api';
import { useExcludeLocalAccountPopup } from '../../hooks/useExcludeLocalAccountsPopup';
import useBulkResumeLocalAccountsProtection from '../../hooks/useBulkResumeLocalAccountsProtectionPopup';
import { useBindLocalAccountPopup } from '../../hooks/useBindLocalAccountsPopup';
import { useUnbindLocalAccountPopup } from '../../hooks/useUnbindLocalAccountsPopup';
import { SnackAlertContextValue } from '../../../../context/SnackAlertProvider';

export interface ContentHeaderProps {
    localAccount: LocalAccount;
    userSession: UserSession;
    onAccountUpdated: () => void;
    snackAlertContext: SnackAlertContextValue;
}

const ContentHeader: FC<ContentHeaderProps> = ({ localAccount, userSession, onAccountUpdated, snackAlertContext }) => {
    const [boundAdEntityGuid, setBoundAdEntityGuid] = useState<string | undefined>(localAccount.boundAdEntity?.guid);
    const [excludeTill, setExcludeTill] = useState<Date | undefined>(localAccount.excludeTill);
    const { pricingModulesState } = usePricingModulesState();
    const mfaModuleEnabled = isMfaModuleEnabled(pricingModulesState);

    useEffect(() => {
        setBoundAdEntityGuid(localAccount.boundAdEntity?.guid);
    }, [localAccount.boundAdEntity?.guid]);

    useEffect(() => {
        setExcludeTill(localAccount.excludeTill);
    }, [localAccount.excludeTill]);

    const { openBindLocalAccountsPopup } = useBindLocalAccountPopup({
        account: localAccount,
        onAccountBound: useCallback(
            (adEntityGuid: Entity['guid']) => {
                setBoundAdEntityGuid(adEntityGuid);
                onAccountUpdated();
            },
            [onAccountUpdated]
        )
    });

    const { openUnbindLocalAccountsPopup } = useUnbindLocalAccountPopup({
        account: localAccount,
        onAccountUnbound: useCallback(() => {
            setBoundAdEntityGuid(undefined);
            onAccountUpdated();
        }, [onAccountUpdated])
    });

    const { openExcludeLocalAccountsPopup } = useExcludeLocalAccountPopup({
        account: localAccount,
        onAccountExcluded: useCallback(
            (excludeTill: LocalAccount['excludeTill']) => {
                setExcludeTill(excludeTill);
                onAccountUpdated();
            },
            [onAccountUpdated]
        ),
        snackAlertContext
    });

    const { resumeLocalAccountsProtection } = useBulkResumeLocalAccountsProtection({
        accounts: [localAccount],
        onProtectionResumed: useCallback(() => {
            setExcludeTill(undefined);
            onAccountUpdated();
        }, [onAccountUpdated])
    });

    const menuItems = useMemo(
        () => (
            <List className="min-w-46 [&]:p-0">
                <ListItem
                    title={boundAdEntityGuid ? 'Edit bound entity' : 'Bind AD accounts'}
                    onClick={openBindLocalAccountsPopup}
                    disabled={!isUserPermitted(userSession, 'matchUsers', ROLES.EDITOR.value)}
                />
                {boundAdEntityGuid && (
                    <ListItem
                        title="Unbind AD accounts"
                        onClick={openUnbindLocalAccountsPopup}
                        disabled={!isUserPermitted(userSession, 'matchUsers', ROLES.EDITOR.value)}
                    />
                )}
                {mfaModuleEnabled && !excludeTill && (
                    <ListItem
                        title="Temporarily exclude"
                        onClick={openExcludeLocalAccountsPopup}
                        disabled={!isUserPermitted(userSession, 'excludeUsers', ROLES.EDITOR.value)}
                    />
                )}
                {mfaModuleEnabled && excludeTill && (
                    <ListItem
                        title="Resume protection"
                        onClick={resumeLocalAccountsProtection}
                        disabled={!isUserPermitted(userSession, 'excludeUsers', ROLES.EDITOR.value)}
                    />
                )}
            </List>
        ),
        [
            openUnbindLocalAccountsPopup,
            openBindLocalAccountsPopup,
            openExcludeLocalAccountsPopup,
            resumeLocalAccountsProtection,
            userSession,
            boundAdEntityGuid,
            mfaModuleEnabled,
            excludeTill
        ]
    );

    return (
        <div className="w-[800px] flex justify-between items-center px-6 py-2 border-b border-sf-gray-200">
            <div className="flex h-14 items-center gap-2">
                <div className="text-14 font-medium">{localAccount.userName}</div>
                {boundAdEntityGuid && <Badge text="Bound" color="secondary" className="font-medium" />}
                <EntityChip
                    text={
                        <div className="flex items-center gap-2">
                            <Status statusType={localAccount.isDisabled ? StatusType.Disabled : StatusType.Success} />
                            <span className="font-medium">{localAccount.isDisabled ? 'Disabled' : 'Enabled'}</span>
                        </div>
                    }
                />
            </div>
            <MenuButton
                className="right-[0]"
                iconClassName="text-sf-gray-400"
                buttonStyle={ButtonStyles.Tertiary}
                buttonSize={ButtonSizes.Medium}
                openByDefault={false}
                content={menuItems}
            />
        </div>
    );
};

export default ContentHeader;
